// One-click "Thank you" email to a fan who tipped. Posts to /api/tips/thank-you
// which validates the fan has actually tipped, enforces a 24h cooldown, checks
// the resend_per_creator_per_day rate limit, and dispatches via Resend.
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, PaperPlaneTilt, Heart } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ThankYouModal({ open, fan, onClose, onSent }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open || !fan) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/tips/thank-you", {
        to_email: fan.email,
        message: message.trim() || undefined,
      });
      toast.success(`Thank-you sent to ${fan.email}.`);
      onSent?.(fan.email, r.data.sent_at);
      setMessage("");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the email.");
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose} data-testid="thank-you-modal"
      >
        <motion.div
          className="modal-safe bg-white rounded-xl border hairline shadow-2xl w-full max-w-md p-6"
          initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-3">
            <h3 className="font-serif-display text-2xl text-[#1C1B1A] flex items-center gap-2">
              <Heart size={22} weight="duotone" className="text-[#C16E5A]" />
              Say thanks
            </h3>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[#F0F1EE]" data-testid="thank-you-close">
              <X size={16} />
            </button>
          </div>
          <p className="text-xs text-[#6B6A68] mb-4">
            Sending a thank-you email to <span className="font-medium text-[#1C1B1A]">{fan.email}</span>
            {fan.total ? <> — who's tipped you <span className="font-medium text-[#1C1B1A]">${(fan.total/100).toFixed(2)}</span> across <span className="font-medium text-[#1C1B1A]">{fan.count}</span> {fan.count === 1 ? "tip" : "tips"}</> : null}.
            Your name is auto-inserted; you can add an optional personal line below.
          </p>

          <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">
            Personal note (optional, ≤500 chars)
          </label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value.slice(0, 500))} rows={4}
            placeholder="e.g., Your tip helped me buy more API credits — really appreciate it!"
            data-testid="thank-you-message"
            className="w-full bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#C16E5A] mb-1" />
          <div className="text-[10px] text-[#6B6A68] mb-4 text-right">{message.length}/500</div>

          <button onClick={submit} disabled={busy} data-testid="thank-you-submit"
            className="w-full text-sm py-3 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60 flex items-center justify-center gap-1.5">
            <PaperPlaneTilt size={14} weight="duotone" />
            {busy ? "Sending…" : "Send thank-you"}
          </button>
          <p className="text-[10px] text-[#6B6A68] mt-2 text-center">
            Each fan can only be thanked once every 24 hours to keep your fans' inboxes happy.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
